<template>
  <div
    class="form-input-number"
    :class="{
      'is-invalid': state === false,
      disabled: $attrs.disabled === ''
    }"
  >
    <BFormGroup
      :disabled="$attrs.disabled"
      :label="label"
      :label-for="$attrs.id"
      :invalid-feedback="errorMessage"
      :label-class="{
        'is-invalid': state === false
      }"
    >
      <BInputGroup>
        <BFormInput
          :value="convertedValue"
          :state="state"
          :aria-label="label || $attrs.placeholder"
          type="number"
          v-bind="$attrs"
          v-on="listeners"
          @input="input"
        />

        <template v-slot:append>
          <button
            :disabled="$attrs.disabled"
            @click="update(-1)"
          >
            <SvgIcon
              name="components/minus"
              width="16"
              height="16"
            />
          </button>
          <button
            :disabled="$attrs.disabled"
            @click="update(1)"
          >
            <SvgIcon
              name="components/add"
              width="16"
              height="16"
            />
          </button>
        </template>
      </BInputGroup>
    </BFormGroup>
  </div>
</template>

<script>
import {BFormGroup} from 'bootstrap-vue/src/components/form-group/form-group'
import {BInputGroup} from 'bootstrap-vue/src/components/input-group/input-group'
import {BFormInput} from 'bootstrap-vue/src/components/form-input/form-input'
import SvgIcon from 'vue-svgicon/dist/components/svgicon.common'

import '../../icons/components/minus'
import '../../icons/components/add'

export default {
  components: {
    BFormGroup,
    BInputGroup,
    BFormInput,
    SvgIcon,
  },
  inheritAttrs: false,

  props: {
    /**
     * The current value of the form input. Result will always be a number.
     */
    value: {
      type: [String, Number],
      default: undefined,
    },

    /**
     * Text to place in the label/legend of the form group.
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * Controls the validation state appearance of the component. 'true' for valid, 'false' or 'null' for no validation state.
     */
    isInvalid: {
      type: Boolean,
      default: false,
    },

    /**
     * Text to show when the form group has an invalid state.
     */
    errorMessage: {
      type: String,
      default: undefined,
    },
  },

  computed: {
    convertedValue () {
      if ((this.value === '0') | (this.value === 0)) {
        return 0
      } else if (this.value) {
        return this.value | 0
      } else {
        return undefined
      }
    },
    state () {
      if (this.errorMessage && this.errorMessage.length > 0) {
        return false
      } else if (this.isInvalid) {
        return false
      } else {
        return null
      }
    },
    listeners () {
      const {input, ...listeners} = this.$listeners
      return listeners
    },
  },

  methods: {
    update (variation) {
      const number = this.convertedValue ? Number(this.convertedValue) : 0
      const newValue = number + variation

      if (number > newValue) {
        /**
         * Input event triggered by user interaction. Emitted after the value is decremented.
         */
        this.$emit('decrement', {oldValue: number, newValue})
      } else {
        /**
         * Input event triggered by user interaction. Emitted after the value is incremented.
         */
        this.$emit('increment', {oldValue: number, newValue})
      }

      this.input(newValue)
    },
    input (number) {
      /**
       * Input event triggered by user interaction. Emitted after the v-model is updated.
       */
      this.$emit('input', number)
    },
  },
}
</script>

<style lang="scss">


.form-input-number {
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
  }

  &:focus,
  &.focus {
    outline: none;

    .form-group {
      border: 2px solid $cyan-500;
      border-radius: 2px;
    }

    .form-control {
      padding-left: subtract($input-padding-x, 2px);
      padding-top: subtract($input-padding-y, 2px);
      padding-bottom: subtract($input-padding-y, 2px);
      height: subtract($input-height, 4px);
    }

    button {
      &:last-child {
        width: subtract(40, 2px);
      }
    }
  }

  &.disabled,
  .disabled {
    pointer-events: none;
  }

  &:hover:not(.is-invalid) {
    .form-control,
    .form-control:focus {
      border-color: $gray-700 !important;
    }

    button {
      border-color: $gray-700;
      color: $gray-800;
    }
  }

  &.is-invalid {
    button {
      border-color: $red-500;
    }
  }

  .form-control {
    border-right: none !important;

    &:disabled {
      pointer-events: none;
    }

    &:hover:not(.is-invalid):not(:invalid) {
      border-color: transparent;
    }

    &:focus {
      border-color: $gray-200;
      padding: $input-padding-y $input-padding-x;
      border-width: 1px;
    }

    &[readonly] {
      ~ .input-group-append button {
        background-color: $gray-000;
      }
    }
  }

  .input-group-append {
    margin: 0;
  }

  button {
    display: inline-block;
    font-weight: 400;
    font-size: $font-size-sm;
    color: $gray-300;
    text-align: center;
    white-space: $btn-white-space;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    width: 40px;
    max-width: 40px;

    border: 1px solid $gray-200;
    // border-right: none;

    padding-left: 0;
    padding-right: 0;

    line-height: $btn-line-height;
    letter-spacing: normal;

    border-top-right-radius: $btn-border-radius;
    border-bottom-right-radius: $btn-border-radius;

    @include transition($input-transition);

    &:focus,
    &.focus {
      outline: 0;
    }

    // Disabled comes first so active can properly restyle
    &.disabled,
    &:disabled {
      color: $gray-300;
      background-color: $gray-100;
      border-color: $gray-200;
      cursor: default;
    }

    &:first-child {
      margin-right: -1px;
    }
  }
}
</style>
